import { reactive } from "vue";
import { decodeJwt, rolesFromPayload } from "../jwt.js";
const DEFAULT_SCOPE = "openid profile email";
export class Auth0Provider {
    name = "auth0";
    state;
    domain;
    clientId;
    tokenStorageKey;
    pkceStorageKey;
    audience;
    scope;
    redirectUri;
    constructor(opts) {
        if (!opts.domain) {
            throw new Error("Auth0Provider: domain is required");
        }
        if (!opts.clientId) {
            throw new Error("Auth0Provider: clientId is required");
        }
        this.domain = normalizeAuth0Domain(opts.domain);
        this.clientId = opts.clientId;
        this.tokenStorageKey = opts.tokenStorageKey;
        this.pkceStorageKey = opts.pkceStorageKey;
        this.audience = opts.audience;
        this.scope = opts.scope ?? DEFAULT_SCOPE;
        this.redirectUri = opts.redirectUri ?? `${window.location.origin}/auth/callback`;
        this.state = reactive({
            status: "idle",
            user: null,
            idToken: null,
            accessToken: null,
            error: null,
        });
    }
    async init() {
        this.state.status = "initializing";
        const tokenSet = this.readTokenSet();
        if (tokenSet) {
            const user = userFromIdToken(tokenSet.idToken);
            if (user) {
                this.state.user = user;
                this.state.idToken = tokenSet.idToken;
                this.state.accessToken = tokenSet.accessToken;
                this.state.status = "authenticated";
                return;
            }
            sessionStorage.removeItem(this.tokenStorageKey);
        }
        this.state.status = "unauthenticated";
    }
    async login(_provider, redirectTo) {
        const pkce = await createPkceState(redirectTo);
        sessionStorage.setItem(this.pkceStorageKey, JSON.stringify(pkce));
        const params = new URLSearchParams({
            response_type: "code",
            client_id: this.clientId,
            redirect_uri: this.redirectUri,
            scope: this.scope,
            state: pkce.state,
            code_challenge: await sha256Base64Url(pkce.codeVerifier),
            code_challenge_method: "S256",
        });
        if (this.audience) {
            params.set("audience", this.audience);
        }
        window.location.assign(`${this.domain}/authorize?${params.toString()}`);
    }
    async logout() {
        sessionStorage.removeItem(this.tokenStorageKey);
        sessionStorage.removeItem(this.pkceStorageKey);
        this.state.user = null;
        this.state.idToken = null;
        this.state.accessToken = null;
        this.state.status = "unauthenticated";
    }
    async handleCallback(url) {
        const error = url.searchParams.get("error");
        if (error) {
            this.state.status = "error";
            this.state.error = url.searchParams.get("error_description") ?? error;
            return "/";
        }
        const code = url.searchParams.get("code");
        const state = url.searchParams.get("state");
        const pkce = this.readPkceState();
        if (!code || !state || !pkce || pkce.state !== state) {
            this.state.status = "error";
            this.state.error = "Auth0 Callback ist ungueltig oder abgelaufen.";
            return "/";
        }
        const tokenResponse = await this.exchangeCode(code, pkce.codeVerifier);
        if (!tokenResponse.id_token) {
            this.state.status = "error";
            this.state.error = tokenResponse.error_description ?? tokenResponse.error ?? "Auth0 hat kein ID-Token geliefert.";
            return "/";
        }
        const expiresIn = tokenResponse.expires_in ?? 3600;
        const tokenSet = {
            accessToken: tokenResponse.access_token ?? null,
            idToken: tokenResponse.id_token,
            expiresAt: Date.now() + expiresIn * 1000,
        };
        sessionStorage.setItem(this.tokenStorageKey, JSON.stringify(tokenSet));
        sessionStorage.removeItem(this.pkceStorageKey);
        const user = userFromIdToken(tokenSet.idToken);
        this.state.user = user;
        this.state.idToken = tokenSet.idToken;
        this.state.accessToken = tokenSet.accessToken;
        this.state.status = user ? "authenticated" : "error";
        this.state.error = user ? null : "Auth0 ID-Token konnte nicht gelesen werden.";
        return pkce.redirectTo;
    }
    async exchangeCode(code, codeVerifier) {
        const response = await fetch(`${this.domain}/oauth/token`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                grant_type: "authorization_code",
                client_id: this.clientId,
                code,
                code_verifier: codeVerifier,
                redirect_uri: this.redirectUri,
            }),
        });
        return (await response.json());
    }
    readTokenSet() {
        const raw = sessionStorage.getItem(this.tokenStorageKey);
        if (!raw)
            return null;
        try {
            const tokenSet = JSON.parse(raw);
            if (!tokenSet.idToken || tokenSet.expiresAt <= Date.now())
                return null;
            return tokenSet;
        }
        catch {
            return null;
        }
    }
    readPkceState() {
        const raw = sessionStorage.getItem(this.pkceStorageKey);
        if (!raw)
            return null;
        try {
            return JSON.parse(raw);
        }
        catch {
            return null;
        }
    }
}
async function createPkceState(redirectToOverride) {
    const currentUrl = new URL(window.location.href);
    const redirectParam = currentUrl.searchParams.get("redirect");
    const redirectTo = redirectToOverride ?? (currentUrl.pathname === "/login" && redirectParam ? redirectParam : `${window.location.pathname}${window.location.search}${window.location.hash}`);
    return {
        codeVerifier: randomBase64Url(64),
        state: randomBase64Url(32),
        redirectTo: redirectTo || "/",
    };
}
function userFromIdToken(idToken) {
    const payload = decodeJwt(idToken);
    if (!payload)
        return null;
    return {
        id: payload.sub,
        email: payload.email,
        name: payload.name,
        picture: payload.picture,
        hostedDomain: payload.hd,
        roles: rolesFromPayload(payload),
    };
}
function randomBase64Url(byteLength) {
    const bytes = new Uint8Array(byteLength);
    crypto.getRandomValues(bytes);
    return base64Url(bytes);
}
async function sha256Base64Url(value) {
    const bytes = new TextEncoder().encode(value);
    const hash = await crypto.subtle.digest("SHA-256", bytes);
    return base64Url(new Uint8Array(hash));
}
function base64Url(bytes) {
    let binary = "";
    bytes.forEach((byte) => {
        binary += String.fromCharCode(byte);
    });
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
export function normalizeAuth0Domain(domain) {
    const trimmed = domain.trim().replace(/\/$/, "");
    if (/^https?:\/\//i.test(trimmed)) {
        return trimmed;
    }
    return `https://${trimmed}`;
}
//# sourceMappingURL=auth0.js.map